import { StaticImageData } from 'next/image';

import React, { ComponentType, ReactNode, useMemo } from 'react';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { KarmaPoint } from '@uikit/icons/KarmaPoint';
import { MsqToken } from '@uikit/icons/MsqToken';
import { PersonCircle } from '@uikit/icons/PersonCircle';

import { Categories, MagicWallCard } from '@shared/api/types/magicWall';
import NextLink from '@shared/common/components/NextLink';
import { formatAmountDisplay } from '@shared/helpers/number';

import { icons, OFFER_STATUS_CONFIG } from './constants';
import { RewardsIcon } from './icons';

enum REWARDS {
  token = 'token',
  karma = 'karma',
}

interface Props {
  card: MagicWallCard;
  showStatus?: boolean;
}

type Category = {
  id: number;
  key: string;
  content: string;
};

type IconKey = keyof typeof icons;

export const getIcon = (iconName: string | StaticImageData, hasOfferCategory: boolean) => {
  if (!hasOfferCategory || typeof iconName !== 'string' || !(iconName in icons)) {
    return icons.default;
  }

  return icons[iconName as IconKey];
};

const OfferStatus = ({
  status,
  offerId,
}: {
  status: keyof typeof OFFER_STATUS_CONFIG;
  offerId: string;
}) => {
  if (!Object.prototype.hasOwnProperty.call(OFFER_STATUS_CONFIG, status)) {
    return null;
  }

  const { label = '', Icon = '', className = '' } = OFFER_STATUS_CONFIG[status];

  const Status = () => (
    <span
      className={classNames('flex gap-2 items-center text-sm font-semibold', className)}
      data-qa="magic-board-offer-completion"
    >
      <Icon className="icons icon-sm" />
      {label}
    </span>
  );

  if (status !== 'REJECTED') {
    return <Status />;
  }

  return (
    <NextLink href={`/magic-board/${offerId}`}>
      <Status />
    </NextLink>
  );
};

const MagicOrderWallOfferCard = ({ card, showStatus }: Props) => {
  const hasKarma = Boolean(card.attributes.rewardKarma);
  const hasRewardAmount = Boolean(card.attributes.rewardAmount);
  const isTokenRewardDisabled = Boolean(card.attributes.tokenRewardDisabled);

  const rewards = useMemo(() => {
    const result: {
      id: string;
      content: ReactNode;
      Icon: ComponentType<{ className?: string }> | null;
    }[] = [
      {
        id: REWARDS.token,
        content:
          hasRewardAmount && !isTokenRewardDisabled
            ? `${card.attributes.rewardAmount} $${card.attributes.rewardSymbol}`
            : null,
        Icon: MsqToken,
      },
      {
        id: REWARDS.karma,
        content: hasKarma ? `${formatAmountDisplay(card.attributes.rewardKarma)} Karma` : null,
        Icon: hasKarma ? KarmaPoint : null,
      },
    ];

    return result;
  }, [card]);

  const isOfferCategoryArray = Array.isArray(card.offerCategory);

  const categories = useMemo(() => {
    const result: Category[] = isOfferCategoryArray
      ? (card.offerCategory as Categories[]).map((category) => ({
          id: category.id,
          key: category.attributes.key,
          content: category.attributes.name,
        }))
      : [
          {
            id: (card.offerCategory as Categories)?.id,
            key: (card.offerCategory as Categories)?.attributes?.key,
            content: (card.offerCategory as Categories)?.attributes?.name,
          },
        ];

    return result;
  }, [card.offerCategory]);

  const hasOfferCategory = Boolean(card.offerCategory);

  const submissions = card.attributes.submissionsLimit
    ? `${card.attributes.submissionsCurr}/${card.attributes.submissionsLimit}`
    : card.attributes.submissionsCurr || 0;
  const iconName = categories[0].key;

  return (
    <NextLink
      href={`/magic-board/${card.id}`}
      className="flex max-sm:flex-col p-4 min-h-[136px] rounded-lg border gap-4 sm:gap-2 border-primary-200 items-start"
    >
      <ImageProxy
        width={500}
        height={500}
        quality={80}
        src={getIcon(iconName, hasOfferCategory)}
        alt="Offer's icon"
        className="flex-shrink-0 aspect-square w-[90px] object-contain"
      />
      <div className="flex flex-col flex-1 min-w-0 max-w-full sm:max-w-[820px]">
        <p
          className="text-lg font-semibold mb-1 truncate max-w-full"
          data-qa="magic-board-offer-name"
        >
          {card.attributes.name}
        </p>
        <p
          className="truncate text-neutral-500 text-xs font-light mb-4 max-w-full"
          data-qa="magic-board-offer-description"
        >
          {card.attributes.description}
        </p>

        <ul className="flex gap-5 sm:gap-2 mt-auto flex-wrap items-center">
          <div
            className="flex items-center font-semibold min-w-0"
            data-qa="magic-board-offer-submissions"
          >
            <PersonCircle className="icon-xs mx-4" />
            Submissions {submissions}
          </div>
          {hasOfferCategory &&
            categories.map((category) => (
              <li
                key={category.id}
                className="flex justify-center items-center bg-[#F8F8F8] rounded-lg min-h-8 font-light px-2 py-1 gap-2.5 text-nowrap"
                data-qa="magic-board-offer-tag"
              >
                {category.content}
              </li>
            ))}
        </ul>
      </div>

      <div className="flex flex-col gap-2 w-[125px] sm:h-[90px] sm:px-2 max-sm:w-full md:ml-auto rounded-lg">
        {showStatus && <OfferStatus status={card.attributes.status} offerId={card.id} />}

        {!showStatus && (
          <span className="flex gap-1 text-neutral-500">
            <RewardsIcon className="icon-[18px]" />
            Rewards
          </span>
        )}

        <ul className="flex flex-col gap-2">
          {rewards.map(({ id, content, Icon }) => (
            <>
              <li
                key={id}
                className={classNames(
                  'flex gap-1 p-2 rounded-2xl text-xs justify-center items-center',
                  id === REWARDS.token ? ' text-neutral-50 bg-secondary-700' : 'bg-primary-100',
                  {
                    hidden: !content,
                  },
                )}
                data-qa="magic-board-offer-reward"
              >
                {Icon && <Icon className="icon-xs flex-shrink-0" />}
                {content}
              </li>
            </>
          ))}
        </ul>
      </div>
    </NextLink>
  );
};

export default MagicOrderWallOfferCard;
